// @ts-nocheck
import { useRouteMatch } from '@shared/core';
import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import { useMemo } from 'react';
import type { LoadMoreProductsClickedProps } from '.';

import type {
  ExpandMobileShopMenuClickProps,
  ProductCatalogCategoryProps,
  ShopNavigateClickedProps,
  AddRegistryItemClickedProps,
  ShopItemClickedProps,
  ShopCurationClickedProps,
  ItemVariantSelectedProps,
  BuyNowClickedProps,
  OpenSearchCatalogClickedProps,
  CancelSearchCatalogClickedProps,
  ClearSearchCatalogClickedProps,
  SearchCatalogClickedProps,
  CatalogProductsIndexQueryErrorArgs,
  ProductDetailsQueryErrorArgs,
  ShopFilterButtonClickedArgs,
  SearchBarFocusedArgs,
  ShopRootExpertsCarouselItemClicked,
  CurationWatchVideoCtaClicked,
  ShopRootExpertsCarouselArrowClicked,
  CurationVideoPlaybackStarted,
  CurationVideoPlaybackPaused,
  CurationVideoPlaybackResumed,
  CurationVideoPlaybackCompleted,
  CurationVideoContentStarted,
  CurationVideoContentPlaying,
  CurationVideoContentCompleted,
  ShopRootExpertsExploreCurationClicked,
  RegistryValuePropTopBarClickedProps,
  RegistryValuePropClickedProps,
  ValuePropDrawerPageProps,
  ShopNavigatorClicked,
  UniversalSearchInitiatedProps,
} from './Catalog.telemetry.types';
import type { ProductCatalogStorefrontStatus } from '@graphql/generated';
import { RegistryItemSource } from './Catalog.controller';
import type { OnFilterSelectArgs } from './components/RefinementFacet/RefinementFacet.types';

const category = 'shop';
const adminShopPagePrefix = 'admin';
const publicShopPagePrefix = 'newmarketing';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createObjectWithPagePrefix = (target: any, source: any[]) => {
  if (typeof source[source.length - 1] === 'object') {
    return Object.assign(target, ...source);
  }

  return target;
};

export const catalogRegistryTelemetry = createTelemetryObject({
  actions: {
    shopNavigateClicked: (clickInfo: ShopNavigateClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: clickInfo.page,
          action: 'ShopNavigate',
          actionType: 'click',
          extraInfo: {
            ...clickInfo,
          },
        },
        args,
      ),
    expandMobileShopMenuClick: (clickInfo: ExpandMobileShopMenuClickProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'ExpandMobileShopNav',
          actionType: 'click',
          extraInfo: {
            ...clickInfo,
          },
        },
        args,
      ),
    shopItemClicked: ({ label, categoryProp, ...clickProps }: ShopItemClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category: categoryProp || category,
          action: 'ShopItem',
          actionType: 'click',
          page: clickProps.page,
          label: label || 'ProductCatalogItemId',
          extraInfo: clickProps,
        },
        args,
      ),
    shopCurationClicked: (clickProps: ShopCurationClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category: 'shop',
          page: clickProps.page,
          action: 'ShopCuration',
          actionType: 'click',
          label: clickProps.label,
          extraInfo: clickProps,
        },
        args,
      ),
    addRegistryItemClicked: ({ page, categoryOverride = category, ...restProps }: Omit<AddRegistryItemClickedProps, 'eventId'> & { categoryOverride?: string }, ...args) => {
      if (!!restProps.isOnboarding && categoryOverride) {
        args[0].category = categoryOverride;
      }
      return createObjectWithPagePrefix(
        {
          category: categoryOverride,
          action: 'AddRegistryItem',
          actionType: 'click',
          page,
          value: restProps.priceValueInMinorUnits,
          label: restProps.method,
          extraInfo: {
            ...restProps,
            metadataSource: RegistryItemSource.catalogShop,
          },
        },
        args,
      );
    },
    itemVariantSelected: ({ page, ...clickProps }: ItemVariantSelectedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'ItemVariant',
          actionType: 'select',
          page,
          extraInfo: clickProps,
        },
        args,
      ),
    buyNowClicked: (clickProps: BuyNowClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'BuyNow',
          actionType: 'click',
          page: clickProps.page,
          label: clickProps.productCatalogItemId,
          extraInfo: clickProps,
        },
        args,
      ),
    loadMoreProductsClicked: ({ page, ...clickProps }: LoadMoreProductsClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'LoadMoreProducts',
          actionType: 'click',
          page: page,
          extraInfo: clickProps,
        },
        args,
      ),
    catalogProductsIndexQueryError: (errorArgs: CatalogProductsIndexQueryErrorArgs, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CatalogProductsIndexQuery',
          actionType: 'error',
          label: errorArgs.categoryLocator || undefined,
          extraInfo: {
            error: errorArgs.error,
          },
        },
        args,
      ),
    catalogProductDetailsQueryError: (errorArgs: ProductDetailsQueryErrorArgs, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CatalogProductDetailsQuery',
          actionType: 'error',
          label: errorArgs.catalogItemId,
          extraInfo: {
            error: errorArgs.error,
          },
        },
        args,
      ),
    openSearchCatalogClicked: ({ page, ...clickProps }: OpenSearchCatalogClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'OpenSearchCatalog',
          actionType: 'click',
          page,
          extraInfo: clickProps,
        },
        args,
      ),
    searchTrendsOptionClicked: (searchTrendValue: string) => ({
      action: 'ButtonInteracted',
      category: 'shop',
      label: 'SearchOption',
      pagePrefix: adminShopPagePrefix,
      extraInfo: {
        value: searchTrendValue,
      },
    }),
    cancelSearchCatalogClicked: ({ page, ...clickProps }: CancelSearchCatalogClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CancelSearchCatalog',
          actionType: 'click',
          page,
          extraInfo: clickProps,
        },
        args,
      ),
    clearSearchCatalogClicked: ({ page, ...clickProps }: ClearSearchCatalogClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'ClearSearchCatalog',
          actionType: 'click',
          page,
          extraInfo: clickProps,
        },
        args,
      ),
    searchCatalogClicked: ({ page, ...clickProps }: SearchCatalogClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'SearchCatalog',
          actionType: 'click',
          page,
          extraInfo: clickProps,
        },
        args,
      ),

    universalSearchInitiated: ({ page, ...clickProps }: UniversalSearchInitiatedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'UniversalCatalogSearchInitiated',
          page,
          extraInfo: clickProps,
        },
        args,
      ),
    // For trackTransaction event criteo is looking for Order Completed action in segment criteo-app-web-events
    onRegistryItemAdded: (itemProps: { label: string; page: string }, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'Order Completed',
          label: itemProps.label,
          page: itemProps.page,
          extraInfo: {
            order_id: Date.now(),
            products: [{ product_id: 1, quantity: 1, price: 0 }],
          },
        },
        args,
      ),
    refinementPanelToggleClicked: (clickProps: ShopFilterButtonClickedArgs, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'ButtonInteracted',
          label: 'ShopFilterButton',
          actionType: 'click',
          page: clickProps.page,
          extraInfo: clickProps,
        },
        args,
      ),
    clickFilterCheckboxValue: (filter: OnFilterSelectArgs, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'ButtonInteracted',
          label: 'FilterAccordion',
          actionType: 'click',
          extraInfo: filter,
        },
        args,
      ),
    searchBarFocused: (clickProps: SearchBarFocusedArgs, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'SearchBarFocused',
          page: clickProps.page,
          extraInfo: clickProps,
        },
        args,
      ),
    shopNavigatorClicked: (clickProps: ShopNavigatorClicked, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'StorefrontNavigator',
          actionType: 'click',
          page: clickProps.page,
          extraInfo: clickProps,
        },
        args,
      ),
    curationsHeroSectionBrowseAllClicked: (...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationsHeroSectionBrowseAll',
          actionType: 'click',
          page: 'root',
        },
        args,
      ),
    curationsHeroSectionCarouselItemClicked: (clickProps: ShopRootExpertsCarouselItemClicked, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationsHeroSectionCarouselItem',
          actionType: 'click',
          page: 'root',
          extraInfo: clickProps,
        },
        args,
      ),
    curationsHeroSectionCarouselArrowClicked: (clickProps: ShopRootExpertsCarouselArrowClicked, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationsHeroSectionCarouselArrow',
          actionType: 'click',
          page: 'root',
          extraInfo: clickProps,
        },
        args,
      ),
    curationsHeroSectionExploreCurationClicked: (clickProps: ShopRootExpertsExploreCurationClicked, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationsHeroSectionExploreCuration',
          actionType: 'click',
          page: 'root',
          method: clickProps.method,
          label: clickProps.method,
          extraInfo: clickProps,
        },
        args,
      ),
    watchCurationVideoClicked: (clickProps: CurationWatchVideoCtaClicked, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'WatchCurationVideo',
          actionType: 'click',
          page: clickProps.page,
          label: clickProps.method,
          extraInfo: clickProps,
        },
        args,
      ),

    valuePropTopBarClicked: (clickProps: RegistryValuePropTopBarClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'RegistryValuePropTopBar',
          actionType: 'click',
          method: clickProps.method,
          extraInfo: clickProps,
        },
        args,
      ),
    valuePropClicked: (clickProps: RegistryValuePropClickedProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'RegistryValueProp',
          actionType: 'click',
          method: clickProps.method,
          extraInfo: clickProps,
        },
        args,
      ),

    // Video player telemetry

    curationVideoPlaybackStarted: (clickProps: CurationVideoPlaybackStarted, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationVideoPlaybackStarted',
          extraInfo: clickProps,
        },
        args,
      ),
    curationVideoPlaybackPaused: (clickProps: CurationVideoPlaybackPaused, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationVideoPlaybackPaused',
          extraInfo: clickProps,
        },
        args,
      ),
    curationVideoPlaybackResumed: (clickProps: CurationVideoPlaybackResumed, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationVideoPlaybackResumed',
          extraInfo: clickProps,
        },
        args,
      ),
    curationVideoPlaybackCompleted: (clickProps: CurationVideoPlaybackCompleted, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationVideoPlaybackCompleted',
          extraInfo: clickProps,
        },
        args,
      ),

    curationVideoContentStarted: (clickProps: CurationVideoContentStarted, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationVideoContentStarted',
          extraInfo: clickProps,
        },
        args,
      ),
    curationVideoContentPlaying: (clickProps: CurationVideoContentPlaying, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationVideoContentPlaying',
          extraInfo: clickProps,
        },
        args,
      ),
    curationVideoContentCompleted: (clickProps: CurationVideoContentCompleted, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'CurationVideoContentCompleted',
          extraInfo: clickProps,
        },
        args,
      ),
    onBrandClicked: ({
      destinationProductCatalogCategoryId,
      destinationProductCatalogCategoryPublicUrlPath,
      destinationProductCatalogCategoryTitle,
    }: {
      destinationProductCatalogCategoryId: string | undefined;
      destinationProductCatalogCategoryPublicUrlPath: string | undefined;
      destinationProductCatalogCategoryTitle: string | undefined;
    }) => {
      return {
        action: 'ShopNavigate',
        actionType: 'click',
        category: 'shop',
        extraInfo: {
          navigationSource: 'brandList',
          destinationProductCatalogCategoryId,
          destinationProductCatalogCategoryPublicUrlPath,
          destinationProductCatalogCategoryTitle,
          destinationProductCatalogCategoryType: 'brand',
        },
      };
    },

    toggleRecentlyAddedRegistryItemUpdateDrawerSetting: (clickProps: { name: 'RecentlyAddedRegistryItemUpdateDrawer.markAsMostWanted'; value: boolean }, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'ButtonInteracted',
          extraInfo: {
            ...clickProps,
          },
        },
        args,
      ),
    saveRecentlyAddedRegistryItemUpdateDrawerSettings: (clickProps: {}, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          action: 'ButtonInteracted',
          extraInfo: {
            name: 'RecentlyAddedRegistryItemUpdateDrawer.saveChanges',
          },
        },
        args,
      ),
    relatedProductCollectionsClicked: ({ productId, productName }: { productId: string; productName: string }) => ({
      category,
      action: 'ButtonInteracted',
      label: 'RelatedProductCollections',
      extraInfo: { productId, productName },
    }),
  },
  pages: {
    root: (pageProps: ProductCatalogCategoryProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: 'root',
          pagePrefix: adminShopPagePrefix,
          extraPageInfo: pageProps,
        },
        args,
      ),
    department: (pageProps: ProductCatalogCategoryProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: 'department',
          pagePrefix: adminShopPagePrefix,
          extraPageInfo: pageProps,
        },
        args,
      ),
    category: (pageProps: ProductCatalogCategoryProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: 'category',
          pagePrefix: adminShopPagePrefix,
          extraPageInfo: pageProps,
        },
        args,
      ),
    subcategory: (pageProps: ProductCatalogCategoryProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: 'subcategory',
          pagePrefix: adminShopPagePrefix,
          extraPageInfo: pageProps,
        },
        args,
      ),
    productDetail: (pageProps: ProductCatalogCategoryProps & { productCatalogItemId: string; storefrontStatus: Maybe<ProductCatalogStorefrontStatus> }, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: 'productDetail',
          pagePrefix: adminShopPagePrefix,
          extraPageInfo: pageProps,
        },
        args,
      ),
    productDetailModal: (pageProps: ProductCatalogCategoryProps & { productCatalogItemId: string; storefrontStatus: Maybe<ProductCatalogStorefrontStatus> }, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: 'productDetailModal',
          pagePrefix: adminShopPagePrefix,
          extraPageInfo: pageProps,
        },
        args,
      ),

    curationDetail: (pageProps: ProductCatalogCategoryProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: 'curationDetail',
          pagePrefix: adminShopPagePrefix,
          extraPageInfo: pageProps,
        },
        args,
      ),
    curationRoot: (pageProps: ProductCatalogCategoryProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: 'curationRoot',
          pagePrefix: adminShopPagePrefix,
        },
        args,
      ),
    valuePropDrawer: (pageProps: ValuePropDrawerPageProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: 'valuePropDrawer',
          pagePrefix: adminShopPagePrefix,
          extraPageInfo: pageProps,
        },
        args,
      ),
    brandListsPage: (pageProps: ProductCatalogCategoryProps, ...args) =>
      createObjectWithPagePrefix(
        {
          category,
          page: 'brands',
          pagePrefix: adminShopPagePrefix,
          extraPageInfo: pageProps,
        },
        args,
      ),
  },
});

const { TelemetryProvider, useTelemetry } = createTelemetry(catalogRegistryTelemetry, { eventId: '', section: '' });

export type UseCatalogRegistryTelemetryReturn = ReturnType<typeof useCatalogRegistryTelemetry>;
const useCatalogRegistryTelemetry = (...args: Parameters<typeof useTelemetry>) => {
  const adminRouteMatch = useRouteMatch<{ eventHandle: string; subRoute: string }>('/:eventHandle/edit/registry/:subRoute');
  const telem = useTelemetry(...args);
  const pagePrefix = adminRouteMatch ? adminShopPagePrefix : publicShopPagePrefix;
  const categoryOverride = adminRouteMatch ? `registry.${adminRouteMatch.params.subRoute}` : category;

  return useMemo(() => {
    const { updateContext, context, ...analyticFnsMap } = telem;
    const updatedTelemObject = { updateContext, context } as typeof analyticFnsMap & { updateContext: typeof updateContext; context: typeof context };
    Object.keys(analyticFnsMap).forEach(key => {
      if (typeof analyticFnsMap[key] === 'object') {
        updatedTelemObject[key] = {};

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedTelemObject[key]['enter'] = (...args: any[]) => analyticFnsMap[key]['enter'](...args, { pagePrefix, eventId: context?.eventId, category: categoryOverride });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedTelemObject[key]['exit'] = (...args: any[]) => analyticFnsMap[key]['exit'](...args, { pagePrefix, eventId: context?.eventId, category: categoryOverride });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedTelemObject[key] = (...args: any[]) => analyticFnsMap[key](...args, { pagePrefix, eventId: context?.eventId, category: categoryOverride });
      }
    });
    return updatedTelemObject;
  }, [telem, pagePrefix, categoryOverride]);
};

export { TelemetryProvider as CatalogTelemetryProvider, useCatalogRegistryTelemetry };
